import type { WidgetStyle } from '@/utils/widgetStyle';

import { IDocument, IFile, ObjectValues } from '../common';

import { IAiModel } from './aimodel.entities';
import { IAiModelPrompt } from './aiPrompts.entities';
import { ICustomDomain } from './domain.entities';
import { ICustomEmail, ICustomEmailDomain } from './emailDomain.entities';
import { IFeatureUsage } from './user.entities';

export const MessageTypeValues = {
  bot: 'bot',
  user: 'user',
} as const;

export type MessageType = (typeof MessageTypeValues)[keyof typeof MessageTypeValues];

export const UserRoleValues = {
  client: 'client',
  freelancer: 'freelancer',
  admin: 'admin',
} as const;

export type UserRole = (typeof UserRoleValues)[keyof typeof UserRoleValues];

export interface FileAttachment {
  id?: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  s3Url: string;
}

export interface DocumentGenerationAttachment {
  chatDeliverables?: Array<{
    clauseId: string;
    sectionIds: string[];
    rendered: boolean;
    truncated: boolean;
  }>;
  documentId?: string;
  /** Durable generation/refinement run; a READY document can belong to an older revision. */
  jobId?: string;
  status?: string;
  title?: string;
  documentType?: string;
  documentUrl?: string;
  downloadUrls?: {
    docx?: string;
    pdf?: string;
  };
}

/** Intermediate streaming progress while Document Studio builds a document. */
export interface DocumentGenerationProgress {
  stage: 'starting' | 'retrieving_sources' | 'outline_created' | 'drafting' | 'finalizing';
  message?: string;
  completedSections?: number;
  totalSections?: number;
  action?: 'generate' | 'refine';
  /** P5-C: clause checklist folded from `ledger_transition` events. */
  ledger?: Array<{ clauseId: string; text: string; state: string; channel?: 'chat' | 'document' }>;
}

export type ArtifactEditReviewOperation =
  | { kind: 'replace'; before: string; after: string }
  | { kind: 'remove-paragraph'; paragraph: string }
  | { kind: 'body-prefix'; sentence: string }
  | { kind: 'remove-footnotes' };

export interface UploadCreationReviewView {
  id: string;
  documentId?: string;
  requestedByUserId: string;
  conversationId: string;
  status: 'PENDING' | 'CLAIMED' | 'DECLINED' | 'APPLIED' | 'CANCELED';
  expiresAt: string;
  instruction: string;
  outputTitle: string;
  operations: ReadonlyArray<ArtifactEditReviewOperation>;
  creditQuote: {
    estimatedCredits: number;
    baseCredits: number;
    perPageCredits: number;
    targetPageCount: number;
    documentType: string;
    featureName: 'chatbotMessageCredit';
  };
  reviewSha256: string;
}

export interface ArtifactEditReview {
  version: 1;
  purpose: 'review_only';
  proposalSha256: string;
  outputTitle: string | null;
  operations: ReadonlyArray<ArtifactEditReviewOperation>;
}

export interface DocumentEditConfirmation {
  id: string;
  /**
   * Durable confirmation machine states (server-owned). The UI offers the
   * yes/no affordance only while PENDING (or RETRYABLE_FAILED: an earlier
   * application failed retryably and the user may answer again).
   * 'CONFIRMED' is the legacy label of a claimed confirmation.
   */
  status:
    | 'PENDING'
    | 'CONFIRMED'
    | 'CLAIMED'
    | 'APPLIED'
    | 'RETRYABLE_FAILED'
    | 'PERMANENTLY_FAILED'
    | 'DECLINED'
    | 'EXPIRED'
    | 'CANCELED';
  baseRevision?: number;
  documentId: string;
  documentTitle: string;
  documentType?: string;
  instruction: string;
  artifactProposal?: ArtifactEditReview;
  artifactProposalSha256?: string;
  requestedByUserId: string;
  createdAt?: string;
  expiresAt?: string;
  /**
   * Stored IR addresses, verbatim from the confirmation. Null/absent on
   * legacy records (treat as whole-document).
   */
  scope?: string[] | null;
  scopeKind?: 'whole_document' | 'bounded';
  scopeItems?: Array<{ address: string; label: string }>;
  /** Server sentence derived from the stored addresses, not the instruction. */
  scopeNotice?: string;
}

/**
 * The authoritative confirmation status after a decision was processed
 * (overlaid onto the asking message): RETRYABLE_FAILED re-arms the Yes/No
 * buttons; APPLIED / PERMANENTLY_FAILED / DECLINED retire them; CONFIRMED
 * = claimed and still being applied.
 */
export interface DocumentEditConfirmationResolution {
  confirmationId: string;
  status: DocumentEditConfirmation['status'];
  documentId: string;
}

/** Where one obligation is delivered. */
export type AgentJobDeliveryChannel = 'chat' | 'document';

/** One part of a proposed chat/document split, with the channel the server
 * proposed for it. Present on a `channel_split_required` question that offers a
 * per-part binding; the card renders these rows and the answer binds each id. */
export interface AgentJobProposedChannelSpan {
  id: string;
  start: number;
  end: number;
  text: string;
  channel: AgentJobDeliveryChannel;
  /** The part's own words decide its channel, so the other one is not on offer.
   * The server derives this; a client cannot, and one that offered the refused
   * channel would cost the user the question. */
  fixed?: true;
}

/** Public parked-job elicitation. Never includes resumeToken or answer. */
export interface AgentJobElicitationSchema {
  sectionIds?: { enum: Array<{ id: string; title: string }> };
  type?: string;
  title?: string;
  description?: string;
  reason?: string;
  question?: string;
  choices?: string[];
  allowFreeText?: boolean;
  /** A card that asks the same choice about several items at once (the research consent card):
   * every item is answered by id in `decisions`, with one of `choices`. */
  items?: Array<{ id: string; question: string }>;
  action?: { enum?: string[] };
  spans?: Array<{ id?: string; start?: number; end?: number; text: string; confidence?: number }>;
  /** The OFFER. Its presence is what makes a per-part binding answerable; a
   * question without it accepts only `{action}`, and one WITH it refuses a bare
   * confirm — the answer that authorized nothing. */
  channels?: { enum?: string[] };
  /** The subject the offer is about: what the server proposed, per part. */
  proposedChannels?: AgentJobProposedChannelSpan[];
  [key: string]: unknown;
}

/**
 * Everything a client may send as an elicitation answer, in ONE place.
 *
 * It used to be spelled out separately at the card, the chat send handler, the
 * streaming hook and the request type, and they had already drifted —
 * `sectionIds` reached the wire through a spread that TypeScript does not
 * excess-check, declared in one of the four. A key that only some layers know
 * about is a key some layer will drop.
 */
export interface AgentJobElicitationAnswer {
  action?: 'confirm' | 'drop';
  text?: string;
  choice?: string;
  /** Multi-item card: item id → the chosen option, one for every listed item. */
  decisions?: Record<string, string>;
  sectionIds?: string[];
  /** Per-clause routing, keyed by proposed clause id. Sent only for a question
   * that offers it, and then it must cover every offered id. */
  channels?: Record<string, AgentJobDeliveryChannel>;
}

export interface AgentJobElicitation {
  id: string;
  jobId: string;
  kind?: string;
  schema?: AgentJobElicitationSchema;
  status?: 'PENDING' | 'ANSWERED' | 'RESUMED' | 'EXPIRED' | 'CANCELED' | string;
  expiresAt?: string;
  ledgerGoalId?: string | null;
  messageId?: string | null;
  createdAt?: string;
}

export interface Message {
  id: string;
  content: string;
  type: MessageType;
  isLoading?: boolean;
  isForm?: boolean;
  formContent?: React.ReactNode;
  // Optional agent metadata
  citations?: Citation[];
  mcpApprovalRequired?: Array<{
    id: string;
    preview: string;
    details?: string;
    risk: 'READ_ONLY' | 'WRITE' | 'DESTRUCTIVE' | 'UNKNOWN';
    expiresAt?: number;
    // Present on a card saved to the conversation: how it was decided and, once run, how it went.
    status?: 'PENDING' | 'APPROVED' | 'DENIED' | 'EXPIRED' | 'CONSUMED';
    outcome?: 'complete' | 'failed';
  }>;
  // The saved result of an approved action: provider text, shown as plain text, never Markdown.
  isMcpApprovalResult?: boolean;
  // Which provider accounts this answer's tools acted on, recorded when it was saved.
  // A conversation holds only the current selection, so attribution has to live on the
  // message or switching company would relabel every earlier answer.
  mcpAccounts?: Array<{
    connectorName: string;
    accountLabel?: string;
    status?: 'complete' | 'unavailable';
  }>;
  /** Safe summaries of tool calls retained on the answer for later history. */
  mcpActionReports?: Array<{
    connectorName: string;
    toolName: string;
    summary?: string;
    viewUrl?: string;
  }>;
  /** Questions the visitor might ask next, shown under the latest answer (embedded chat). */
  followUps?: string[];
  /** Safe explanations for failed or refused tool calls. */
  mcpNotices?: Array<{
    code: string;
    message: string;
    retryable: boolean;
    connectorName?: string;
    toolName?: string;
  }>;
  // File attachments for chat documents
  attachments?: FileAttachment[];
  /**
   * The attachment arrived by email rather than being chosen by anybody here.
   *
   * Drawn on the assistant's side, with the rest of what the mailbox did. The row is still
   * stored as a user turn — that is what the model replays and what the analytics count —
   * so this is presentation only.
   */
  deliveredByEmail?: boolean;
  // Generated document action metadata for chat-side downloads
  documentGeneration?: DocumentGenerationAttachment;
  // Live progress while Document Studio is generating (streaming only)
  documentGenerationProgress?: DocumentGenerationProgress;
  // Persisted prompt asking whether a natural-language follow-up should edit a draft
  uploadCreationReview?: UploadCreationReviewView;
  documentEditConfirmation?: DocumentEditConfirmation;
  // C3b: parked document-generation elicitation on the pointer message
  agentJobElicitation?: AgentJobElicitation;
  // User feedback for bot messages
  feedback?: 'like' | 'dislike' | null;
  // Shared Session: Sender information for multi-user conversations
  senderId?: string;
  sender?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  // ISO timestamp of when the message was created. Server time for loaded
  // history; client time stamped at send for freshly-created live messages.
  createdAt?: string;
}

export interface IChatBot extends IDocument {
  accessScope?: 'ACCOUNT' | 'CREATOR_ONLY' | null;
  createdByUserId?: string | null;
  name: string;
  chatLimit: number;
  usedNumber: number;
  progress: number;
  isPublic: boolean;
  isActive: boolean;
  aiModelId: string;
  availableModelIds?: string[]; // Models available for staff/users to switch between
  aiModel: IAiModel | null;
  aiPrompt: IAiModelPrompt | null;
  aiPromptId: string;
  appearance: string;
  userId: string;
  derivedFromId?: string | null;
  /** The partner brand Atlas presents this expert under (e.g. "meai"); set by operators, not owners. */
  partnerBrand?: string | null;
  chatBotProfilePictures: IFile[];
  domainActive: boolean;
  domains: string[];
  textPrompt: string | null;
  userFeatureUsage: IFeatureUsage[];
  rateLimit: {
    enabled: boolean;
    messages: number;
    windowMinutes: number;
    errorMessage: string;
  } | null;
  customDomain: ICustomDomain | null;
  customDomainId: string | null;
  activeCustomDomain: ICustomDomain | null;
  activeCustomDomainId: string | null;
  emailDomain: ICustomEmailDomain | null;
  customEmails: ICustomEmail | null;
  customEmailsId: string | null;
  emailDomainId: string | null;

  // Frozen State Fields (Chatbot)
  resourceState?: 'active' | 'frozen' | 'deleted';
  frozenAt?: string;
  frozenReason?: string;
  /** @deprecated Use frozenReason instead */
  freezeReason?: string;
  deleteAt?: string;
  daysUntilDeletion?: number;
  /**
   * Set when this private expert was paused after the account's private-expert capacity dropped. It stays private and
   * answers no one; resuming it is not available in the app yet, even if capacity returns. Null or missing = not paused.
   */
  privateEntitlementHeldAt?: string | null;

  // Frozen State Fields (Knowledge Base)
  kbState?: 'active' | 'frozen' | 'deleted';
  kbFrozenAt?: string;
  kbFrozenReason?: string;
  kbDeleteAt?: string;
  daysUntilKBDeletion?: number;

  // Frozen State Fields (Custom Domain)
  domainResourceState?: 'active' | 'frozen' | 'removed';
  domainFrozenAt?: string;
  domainFrozenReason?: string;
  daysUntilDomainDeletion?: number;

  // Agent / citation controls (may be absent on older bots)
  agentEnabled?: boolean;
  showAgentThinking?: boolean;
  showCitations?: boolean;
  webSearchEnabled?: boolean;
  webSearchPublicEnabled?: boolean;
  webSearchThirdPartyFallbackEnabled?: boolean;
  liveDataEnabled?: boolean;

  // Voice capabilities
  voiceInputEnabled?: boolean;
  voiceOutputEnabled?: boolean;
  autoSpeakResponses?: boolean; // Always auto-speak bot responses (even for text input)
  ttsVoiceId?: string;
  ttsEngine?: 'standard' | 'neural' | 'long-form' | 'generative';
  voiceLanguage?: string | null;
}

export const LAYOUT_TYPES = {
  GRID: 'grid',
  LIST: 'list',
} as const;

export type LayoutType = (typeof LAYOUT_TYPES)[keyof typeof LAYOUT_TYPES];

export interface IMessageResponse {
  message: string;
  response: string;
}

export interface Citation {
  /** Actual retrieved S3 artifact, retained to authorize mapped original PDFs. */
  retrievedSource?: string;
  /** Server-bound reference number; never replace it with the array position. */
  citationNumber?: number;
  text?: string;
  source?: string;
  pageTitle?: string;
  score?: number;
  /** First/last source-PDF page covered by the cited chunk (from KB metadata). */
  pageStart?: number;
  pageEnd?: number;
  /** ISO timestamp of the cited source's last known update (crawl/ingest date). */
  updatedAt?: string;
  /** Kind of knowledge record. Q&A entries and training text have no file to open. */
  sourceType?: 'qna' | 'text' | 'file' | 'web' | 'document';
  /** The cited knowledge record. Identity only; never shown. */
  recordId?: string;
}

export interface IChartBotLeadForm {
  title: string;
  formShownAfterMessage: string;
  nameInput: {
    isVisible: boolean;
    isRequired: boolean;
    placeholder: string;
  };
  emailInput: {
    isVisible: boolean;
    isRequired: boolean;
    placeholder: string;
  };
  phoneInput: {
    isVisible: boolean;
    isRequired: boolean;
    placeholder: string;
    /**
     * Dial code preselected in the country dropdown (e.g. '+46'). Optional —
     * bots configured before this field existed fall back to '+1'. The end
     * user can always pick another country.
     */
    defaultCountryCode?: string;
  };
  messageInput: {
    isVisible: boolean;
    isRequired: boolean;
    placeholder: string;
  };
  submitButtonColor: string;
  /**
   * Label of the submit button (e.g. 'Skicka' for a Swedish bot). Optional —
   * pre-existing bots fall back to 'Submit'.
   */
  submitButtonText?: string;
  thankYouMessage: string;
}

export const CHAT_ICON_ORIENTATION = {
  LEFT: 'left',
  RIGHT: 'right',
} as const;

export type ChatIconOrientation =
  (typeof CHAT_ICON_ORIENTATION)[keyof typeof CHAT_ICON_ORIENTATION];

export const CHAT_ICON_ORIENTATION_MAP = {
  [CHAT_ICON_ORIENTATION.LEFT]: 'Left',
  [CHAT_ICON_ORIENTATION.RIGHT]: 'Right',
} as const;

export interface IChartBotBrandingCustomization {
  logo: string | File;
  chatIconOrientation: string;
  /**
   * The embedded widget's chat style (utils/widgetStyle). Missing means the
   * default style; a style this build doesn't have yet renders as the default.
   */
  widgetStyle?: WidgetStyle;
  /**
   * One color the chat styles derive every shade from (utils/widgetTheme),
   * #rrggbb. Missing falls back to chatWindowHeaderBgColor.
   */
  brandColor?: string;
  chatWindowHeaderBgColor: string;
  chatWindowBgColor: string;
  userMessageBgColor: string;
  botMessageBgColor: string;
  sendMessageBtnColor: string;
  messageSound: {
    isEnabled: boolean;
    sound: string;
  };
  embedLauncher?: {
    enabled?: boolean;
    /** Follow the chat style's launcher look; see EmbedLauncherSettings.matchChatStyle. */
    matchChatStyle?: boolean;
    /** The greeting card above the button; see EmbedLauncherSettings.teaser. */
    teaser?: { enabled?: boolean; delaySeconds?: number };
    style?: 'bubble' | 'pill';
    icon?: 'brand' | 'sparkles' | 'messages' | 'help' | 'bot' | 'headset' | 'bolt' | 'chat';
    label?: string;
    showLabel?: boolean;
    backgroundColor?: string;
    textColor?: string;
    size?: 'sm' | 'md' | 'lg';
    horizontalOffset?: number;
    verticalOffset?: number;
  };
  autoOpen?: {
    enabled?: boolean;
    delaySeconds?: number;
    frequency?: 'always' | 'once_per_session' | 'once_per_day';
    onMobile?: boolean;
  };
}

export interface IChartBotWhiteLabelCustomization {
  hideDefaultBranding: boolean;
  customBranding: {
    isEnabled: boolean;
    brandingText: string;
  };
}

export interface AppearanceFormValues {
  title: string;
  placeholderText: string;
  welcomeMessage: string;
  /**
   * Per-part-of-day greeting text shown above the welcome message. Blank or
   * missing parts fall back to the English defaults, so existing bots are
   * unaffected. The time-of-day switching itself is not configurable.
   */
  greetings?: { morning?: string; afternoon?: string; evening?: string };
  /** Whether to show the time-of-day greeting at all. Defaults to shown. */
  greetingChecked?: boolean;
  /** Whether to show an icon beside the greeting. Defaults to shown. */
  greetingIconChecked?: boolean;
  /**
   * Owner-authored text shown to a VISITOR when the bot's message credits run
   * out. Blank or missing falls back to the built-in English default, so bots
   * that never set it are unaffected. Free text: it may be in any language and
   * may carry its own contact details, so nothing is appended to it.
   */
  unavailableMessage?: string;
  titleChecked: boolean;
  placeHolderChecked: boolean;
  welcomeMessageChecked: boolean;
  suggestions: { value: string }[];
  suggestionsChecked: boolean;
  leadFormChecked: boolean;
  leadFormFields: IChartBotLeadForm;
  brandingCustomization: IChartBotBrandingCustomization;
  whiteLabelCustomization: IChartBotWhiteLabelCustomization;
  // Controls whether this bot should surface citations/footnotes in answers
  showCitations?: boolean;
  webSearchEnabled?: boolean;
  webSearchPublicEnabled?: boolean;
  webSearchThirdPartyFallbackEnabled?: boolean;
  liveDataEnabled?: boolean;
  // Controls whether document upload functionality is enabled for this chatbot
  enableDocumentUpload?: boolean;
  // Controls whether public/embed visitors can choose the conversation AI model
  allowEndUserModelSelection?: boolean;
  // Voice capabilities
  voiceInputEnabled?: boolean;
  voiceOutputEnabled?: boolean;
  autoSpeakResponses?: boolean; // Always auto-speak bot responses
  voiceLanguage?: string | null;
}

export interface IUserAnalyticsResponse {
  messagesConsumed: {
    used: number;
    total: number;
    percentage: number;
    formatted: string;
    isUnlimited: boolean;
  };
  messagesAllocated: {
    allocated: number;
    total: number;
    percentage: number;
    formatted: string;
    isUnlimited: boolean;
  };
  activeBots: {
    active: number;
    total: number;
    percentage: number;
    isUnlimited: boolean;
  };
  trainingCharacters?: {
    current: number;
    total: number;
    percentage: number;
    formatted?: string;
  } | null;
}

export interface IMessageAllocationSummary {
  totalCredits: number;
  allocatedCredits: number;
  availableCredits: number;
  totalConsumed: number;
  bots: {
    id: string;
    name: string;
    limit: number;
    used: number;
    available: number;
  }[];
  isUnlimited: boolean; // Flag indicating if message credits are unlimited
}

export const ANALYTICS_PERIOD = {
  TODAY: 'today',
  THIS_WEEK: 'this_week',
  THIS_MONTH: 'this_month',
  THIS_YEAR: 'this_year',
} as const;

export type TANALYTICS_PERIOD = ObjectValues<typeof ANALYTICS_PERIOD>;

export interface ITotalActiveUsers {
  count: number;
  percentageChange: number;
  isIncrease: boolean;
  label: string;
}

export interface ITotalTimeUsed {
  hours: number;
  minutes: number;
  formatted: number;
  percentageChange: number;
  isIncrease: boolean;
}

export interface IMessageUsed {
  used: number;
  total: number;
  percentage: number;
  percentageChange: number;
  isIncrease: boolean;
  formatted: string;
}

export interface ITrainingCharacters {
  current: number;
  total: number;
  percentage: number;
  percentageChange: number;
  isIncrease: boolean;
}

export interface IActivityChart {
  hour: string;
  users: number;
  messages: number;
  timestamp: string;
}

export interface IBotAnalyticsResponse {
  totalActiveUsers: ITotalActiveUsers;
  totalTimeUsed: ITotalTimeUsed;
  messagesUsed: IMessageUsed;
  trainingCharacters: ITrainingCharacters;
  activityChart: IActivityChart[];
  period: TANALYTICS_PERIOD;
  periodLabel: string;
  chatbot: {
    id: string;
    name: string;
    isActive: boolean;
  };
}

export interface IBotImportStateImports {
  files: number;
  fileTrainings: number;
  urlCrawls: number;
  crawledPages: number;
  qnaPairs: number;
  textTrainings: number;
  audioTranscriptions: number;
  trainedItems: number;
  pendingItems: number;
  failedItems: number;
  totalKbCharacters: number;
  totalUploadBytes: number;
}

export interface IBotImportStateUsage {
  conversations: number;
  messages: number;
  activeUsers: number;
  leads: number;
  handoffs: number;
  likedMessages: number;
  dislikedMessages: number;
  lastConversationAt: string | null;
}

export interface IBotImportStateIntegrations {
  slack: number;
  whatsapp: number;
  telegram: number;
  teams: number;
  notion: number;
  zendesk: number;
  discord: number;
  zohoDesk: number;
  total: number;
  active: number;
}

export interface IBotImportStateResponse {
  id: string;
  name: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  isPublic: boolean;
  chatLimit: number;
  resourceState: string;
  kbState: string;
  tier: string;
  agentEnabled: boolean;
  showCitations: boolean;
  webSearchEnabled?: boolean;
  webSearchPublicEnabled?: boolean;
  webSearchThirdPartyFallbackEnabled?: boolean;
  liveDataEnabled?: boolean;
  isTemplate: boolean;
  derivedFromId: string | null;
  marketplaceListingDraftId: string | null;
  aiModel: IAiModel | null;
  credits: {
    allocatedMessageCredits: number;
    usedMessageCredits: number;
    isUnlimited: boolean;
  };
  imports: IBotImportStateImports;
  usage: IBotImportStateUsage;
  integrations: IBotImportStateIntegrations;
  apiKeys: {
    total: number;
    active: number;
  };
  access?: {
    assignedStaffCount: number;
    totalStaffCount: number;
    assignedStaff: Array<{
      id: string;
      email: string | null;
      name: string | null;
    }>;
    marketplaceLicense: {
      totalSeats: number;
      assignedSeats: number;
      availableSeats: number;
      status: string;
    } | null;
  };
}

export const ACTIVITY_STATUS = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
} as const;

export type ACTIVITY_STATUS_TYPE = (typeof ACTIVITY_STATUS)[keyof typeof ACTIVITY_STATUS];

export const ACTIVITY_STATUS_MAP = {
  [ACTIVITY_STATUS.ACTIVE]: 'Active',
  [ACTIVITY_STATUS.DISABLED]: 'Disabled',
} as const;

// Staff member permissions for chatbot features
export interface FeaturePermission {
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
}

export interface ChatbotPermissions {
  chatLogs: FeaturePermission;
  knowledgeBase: FeaturePermission;
  manageLeads: FeaturePermission;
  qna: FeaturePermission; // QnA permissions are synced with knowledgeBase in the UI
  tuneBot: FeaturePermission;
  appearance: FeaturePermission;
  mcpConnectors: FeaturePermission;
  /**
   * The intake addresses on a bot, and the mail that arrived at them.
   *
   * `edit` is the sensitive action here rather than `view`: it changes the permitted
   * senders, which is the only thing standing between a chosen, guessable address and
   * anyone who can put a false invoice in front of a reviewer.
   */
  emailIntake: FeaturePermission;
}

export type FeatureName = keyof ChatbotPermissions;
export type ActionName = keyof FeaturePermission;

/**
 * Atlas per-bot key (BODR WP-02, contract C5; mirrors apps/api permissions.types.ts `atlas?`).
 * Kept outside ChatbotPermissions/FeatureName until WP-11 adds the "Atlas assistant" grid row and its
 * label together, so the classic staff UI is unchanged. A missing key means OFF.
 */
export const ATLAS_FEATURE_KEY = 'atlas' as const;
export type ChatbotPermissionsWithAtlas = ChatbotPermissions & { atlas?: FeaturePermission };

export const DEFAULT_PERMISSIONS: ChatbotPermissions = {
  chatLogs: { view: false, add: false, edit: false, delete: false },
  knowledgeBase: { view: false, add: false, edit: false, delete: false },
  manageLeads: { view: false, add: false, edit: false, delete: false },
  qna: { view: false, add: false, edit: false, delete: false },
  tuneBot: { view: false, add: false, edit: false, delete: false },
  appearance: { view: false, add: false, edit: false, delete: false },
  mcpConnectors: { view: false, add: false, edit: false, delete: false },
  emailIntake: { view: false, add: false, edit: false, delete: false },
};

export const FULL_ACCESS_PERMISSIONS: ChatbotPermissions = {
  chatLogs: { view: true, add: true, edit: true, delete: true },
  knowledgeBase: { view: true, add: true, edit: true, delete: true },
  manageLeads: { view: true, add: true, edit: true, delete: true },
  qna: { view: true, add: true, edit: true, delete: true },
  tuneBot: { view: true, add: true, edit: true, delete: true },
  appearance: { view: true, add: true, edit: true, delete: true },
  mcpConnectors: { view: true, add: true, edit: true, delete: true },
  emailIntake: { view: true, add: true, edit: true, delete: true },
};

// Chatbot with permissions (returned from API for staff members)
export interface IChatBotWithPermissions {
  id: string;
  name: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  permissions: ChatbotPermissions;
}

// ── Analytics types (used by the /analytics page) ──────────────────────────

export interface ITopQuestion {
  rank: number;
  question: string;
  count: number;
  percentage: number;
}

export interface ITopQuestionsResponse {
  questions: ITopQuestion[];
  totalUniqueQuestions: number;
  totalMessages: number;
  period: string;
  periodLabel: string;
  /** True when the API counted only the latest 10,000 user messages of the range (MESSAGE_FETCH_LIMIT). */
  dataTruncated?: boolean;
}

export interface IKnowledgeGapEntry {
  rank: number;
  question: string;
  dislikeCount: number;
  totalOccurrences: number;
  percentage: number;
}

export interface IKnowledgeGapAnalyticsResponse {
  gaps: IKnowledgeGapEntry[];
  totalDislikedMessages: number;
  totalUserMessages: number;
  unresolvedGaps: number;
  resolvedGaps: number;
  period: string;
  periodLabel: string;
}

export interface IUserEngagementResponse {
  avgSessionDurationSeconds: number;
  avgSessionDurationFormatted: string;
  returnRate: number;
  returningUsers: number;
  newUsers: number;
  totalUniqueUsers: number;
  avgConversationsPerUser: number;
  likeRate: number;
  dislikeRate: number;
  period: string;
  periodLabel: string;
}

export interface IChatbotPerformanceEntry {
  chatbotId: string;
  chatbotName: string;
  totalMessages: number;
  likedMessages: number;
  dislikedMessages: number;
  satisfactionRate: number;
  totalConversations: number;
  positiveConversations: number;
  negativeConversations: number;
}

export interface IChatbotPerformanceResponse {
  chatbots: IChatbotPerformanceEntry[];
  overall: {
    totalMessages: number;
    totalLiked: number;
    totalDisliked: number;
    overallSatisfactionRate: number;
    totalConversations: number;
    positiveConversations: number;
    negativeConversations: number;
  };
  period: string;
  periodLabel: string;
}
